'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import BannerArt from '@/components/BannerArt';
import Footer from '@/components/Footer';
import ScrollRevealInit from '@/app/components/ScrollRevealInit';
import AppImage from '@/components/ui/AppImage';

const awardImages = [
  {
    src: '/assets/images/media-room/DSC6574-scaled.jpg',
    alt: 'Kasturi Food Products award ceremony — team receiving recognition for food manufacturing excellence',
  },
  {
    src: '/assets/images/media-room/DSC6572-scaled.jpg',
    alt: 'Kasturi Food Products award event — founders with award trophy at OTV Business Odisha ceremony',
  },
  {
    src: '/assets/images/media-room/DSC6570-scaled.jpg',
    alt: 'Kasturi Food Products award presentation — team members celebrating at industry recognition event',
  },
  {
    src: '/assets/images/media-room/WhatsApp-Image-2023-08-31-at-10.43.30.jpeg',
    alt: 'Kasturi Food Products team at award function — group photo with award certificate',
  },
];

const videos = [
  { id: 'qehDf6zymUg', title: 'Pioneering Food Manufacturing Excellence in Odisha', subtitle: 'Winner of OTV Business Odisha Award', channel: 'Kasturifoodproducts' },
  { id: 'cEKcLEqVEQ0', title: "Quality that speaks for itself at Kasturi Food\'s stall", subtitle: 'Stall UC45-46', channel: 'Kasturifoodproducts' },
  { id: 'Sp_hW4A-Yk4', title: 'HEALTH & WELLNESS CONCLAVE- 2023', subtitle: 'Featured on OTV', channel: 'OTV' },
  { id: 'Jk9qylGjNBo', title: 'Embracing the Mahashivratri fast with a healthy touch', subtitle: 'Kasturi Foods healthy fasting options', channel: 'Kasturifoodproducts' },
  { id: 'ODo9MeaTqx4', title: 'Atta & Chhatua of Kasturi Foods at Cuttack Baliyatra 2023', subtitle: 'Centre of Attraction for Revelers', channel: 'Argus Digital' },
  { id: 'mgsdq6qaIO0', title: 'Embracing the Mahashivratri fast with a health-conscious approach', subtitle: 'Suitable for all age groups', channel: 'Kasturifoodproducts' },
  { id: 'qzHZwYtiVzM', title: 'Celebrating the revered fast of Mahashivratri with a healthy twist', subtitle: 'Kasturi Foods Mahashivratri special', channel: 'Kasturifoodproducts' },
  { id: 'BNDOPEO99VY', title: 'Embracing the sacred fast of Mahashivratri with a healthy twist', subtitle: 'Kasturi Foods fasting essentials', channel: 'Kasturifoodproducts' },
];

function VideoCard({ video }: { video: typeof videos[0] }) {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div className="relative" style={{ aspectRatio: '16/9', background: '#1a1208' }}>
        {!playing ? (
          <>
            <img
              src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
              alt={`Watch: ${video.title}`}
              className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,18,8,0.7) 0%, transparent 50%)' }} />
            <button
              onClick={() => setPlaying(true)}
              className="absolute inset-0 flex items-center justify-center group"
              aria-label={`Play ${video.title}`}>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                style={{ background: 'var(--accent)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white" className="ml-0.5">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>
            </button>
          </>
        ) : (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0" />
        )}
      </div>
      <div className="p-4">
        <p className="font-display font-[200] text-sm leading-snug mb-1" style={{ color: 'var(--foreground)' }}>{video.title}</p>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{video.subtitle}</p>
        <p className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: 'var(--accent-ink)' }}>{video.channel}</p>
      </div>
    </div>
  );
}

export default function MediaRoomClient() {
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);

  return (
    <main className="grain-overlay bg-background min-h-screen overflow-x-hidden">
      <Header />

      {/* Page Hero */}
      <section
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #2A1015 0%, #3A1820 55%, #2A1015 100%)' }}>
        <BannerArt />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="block text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--accent)' }}>
            Press & Media
          </span>
          <h1
            className="font-display font-[200] text-white leading-none mb-6"
            style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', letterSpacing: '0.02em' }}>
            Media Room
          </h1>
          <p className="text-white/60 text-lg max-w-xl leading-relaxed">
            Awards, news coverage, and media highlights from Kasturi Food Products.
          </p>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-20 md:py-28" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12 reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
              Recognition
            </span>
            <h2
              className="font-display font-[200]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
              Awards &{' '}
              <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>Achievements</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {awardImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setLightboxImg(img.src)}
                className={`reveal stagger-${i + 1} relative rounded-2xl overflow-hidden group cursor-pointer`}
                style={{ aspectRatio: '3/4', background: 'var(--muted)' }}
                aria-label={`View award photo ${i + 1}`}>
                <AppImage
                  src={img.src}
                  alt={img.alt}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105" />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                  style={{ background: 'rgba(42,16,21,0.55)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Videos */}
      <section className="py-16 md:py-24" style={{ background: 'var(--secondary)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12 reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
              In The News
            </span>
            <h2
              className="font-display font-[200]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
              News &{' '}
              <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>Videos</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {videos.map((video, i) => (
              <div key={video.id} className={`reveal stagger-${i % 4 + 1}`}>
                <VideoCard video={video} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.9)' }}
          onClick={() => setLightboxImg(null)}>
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
            onClick={() => setLightboxImg(null)}
            aria-label="Close lightbox">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxImg}
            alt="Award photo enlarged view"
            className="max-w-full max-h-[85vh] rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <Footer />
      <ScrollRevealInit />
    </main>
  );
}
